import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router";
import LanguageSelection from "../../components/LanguageSelection/LanguageSelection";
import { isAllowedToViewUsers } from "../../services/auth/Roles";
import ResetAllParameter from "../../store/resetParameters";
import { logOut, selectCurrentRoles } from "../../store/slices/auth/authSlice";
import { selectProjectId, selectProjectName } from "../../store/slices/projectSlice";
import "./Navbar.scss";
import { saveProject } from "./Settings";
import { ToastContainer, toast } from "react-toastify";
import {
  setCreatedAt,
  setPreparedByCompany,
  setPreparedByUsername,
} from "../../store/slices/deckblattSlice";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "../../store/slices/bemessungenMikropfaehleSlice";
import i18n from "../../i18n";
import { useGetProfileQuery } from "../../services/API/ProfileAPI";
import { setIgnoreMobile } from "../../store/slices/appTitanSlice";
import GeoBIM from "../../containers/GeoBIM/GeoBIM";
import geoBIMImage from "../../images/Icons/geo-bim-klein.jpg";
import {
  collapseWhitespace,
  formatBlockedChars,
  getDisallowedChars,
  stripDisallowedChars,
} from "../../utils/latinCharset";

function BerechnungNavigation() {
  const { t } = useTranslation(["translation", "account", "register"]);
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const roles = useSelector(selectCurrentRoles);
  const projectId = useSelector(selectProjectId);
  const projectName = useSelector(selectProjectName);
  const { data: userProfile, isLoading } = useGetProfileQuery();
  const [formState, setFormState] = useState({});
  const [showSaveProject, setSaveProjectShow] = useState(false);
  const [showGeoBIM, setShowGeoBIM] = useState(false);
  // Disallowed characters removed from the project name, for the hint.
  const [blockedChars, setBlockedChars] = useState([]);
  const handleSaveProjectClose = () => setSaveProjectShow(false);
  const handleGeoBIMClose = () => setShowGeoBIM(false);
  const bemessungenMikropfaehle = useSelector(selectBemessungenMikropfaehle);

  const AS = "as";
  const PROJECT = "project";

  const handleSaveProject = async () => {
    const isProject = formState.saveAs === PROJECT;
    try {
      await saveProject({
        id: isProject ? projectId : "",
        pname: formState.projectName,
      })
        .then((project) => {
          toast.success(
            isProject ? t("projectList.success.updated") : t("projectList.success.new")
          );
        })
        .catch((error) => {
          console.error(error);
          toast.error(t("projectList.error.internalError"));
        });
    } catch (error) {
      console.error();
      toast.error(t("projectList.error.internalError"));
    }
    setSaveProjectShow(false);
  };

  const handleSaveProjectShow = () => {
    setFormState({
      saveAs: projectId === "" ? AS : PROJECT,
      projectName: projectName === "" ? t("projectList.saveModal.newProject") : projectName,
    });
    setSaveProjectShow(true);
  };

  const handleChange = ({ target: { name, value } }) => {
    // Restrict the project/settings name to Latin characters.
    const cleaned = collapseWhitespace(stripDisallowedChars(value));
    setBlockedChars(getDisallowedChars(value));
    return setFormState((prev) => ({ ...prev, [name]: cleaned }));
  };

  const handleRadio = (e) => {
    e.persist();

    setFormState((prevState) => ({
      ...prevState,
      saveAs: e.target.value,
    }));
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className="navbar sticky-top flex-md-nowrap shadow navbar-expand-lg"
        id="dashboard-navigation"
      >
        <div className="container-fluid p-0">
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li
                key={0}
                className={`nav-item navbar-text${isActive("/application/projects") ? " active" : ""}`}
              >
                <div className="nav-link" onClick={() => navigate("/application/projects")}>
                  {t("navbar.projects")}
                </div>
              </li>
              <li key={1} className={`nav-item navbar-text${isActive("#") ? " active" : ""}`}>
                <div
                  className="nav-link"
                  onClick={() => {
                    handleSaveProjectShow();
                  }}
                >
                  {t("navbar.saveProject")}
                </div>
              </li>
              <li key={3} className={`nav-item navbar-text${isActive("#") ? " active" : ""}`}>
                <div
                  className="nav-link"
                  onClick={() => {
                    dispatch(ResetAllParameter());
                    dispatch(setPreparedByCompany(userProfile?.company_name));
                    dispatch(setPreparedByUsername(userProfile?.name + " " + userProfile.surname));
                    dispatch(setCreatedAt(new Date(Date.now()).toLocaleDateString(i18n.language)));
                  }}
                >
                  {t("navbar.resetAllParameters")}
                </div>
              </li>
              <li
                key={4}
                className={`nav-item navbar-text${isActive("/application/createpdf") ? " active" : ""}`}
              >
                <div className="nav-link" onClick={() => navigate("/application/createpdf")}>
                  {t("navbar.createPDF")}
                </div>
              </li>
              {Number(bemessungenMikropfaehle) === BemessungenMikropfaehle.GERMANY && (
                <li
                  key={5}
                  className={`nav-item navbar-text${isActive("/application/createlv") ? " active" : ""}`}
                >
                  <div className="nav-link" onClick={() => navigate("/application/createlv")}>
                    {t("navbar.createLV")}
                  </div>
                </li>
              )}
              {isAllowedToViewUsers(roles) ? (
                <li
                  key={6}
                  className={`nav-item navbar-text${isActive("/dashboard/users") ? " active" : ""}`}
                >
                  <div className="nav-link" onClick={() => navigate("/dashboard/users")}>
                    {t("navbar.settings")}
                  </div>
                </li>
              ) : null}
              <li
                key={7}
                className={`nav-item navbar-text${isActive("/application/account/profile") ? " active" : ""}`}
              >
                <div className="nav-link" onClick={() => navigate("/application/account/profile")}>
                  {t("navbar.account")}
                </div>
              </li>
              <li key={8} className="nav-item navbar-text">
                <div className="nav-link">
                  <LanguageSelection />
                </div>
              </li>
              <li key={9} className="nav-item navbar-text">
                <div
                  className="nav-link"
                  onClick={() => {
                    dispatch(setIgnoreMobile(false));
                    dispatch(logOut());
                  }}
                >
                  <Link to="#">{t("navbar.logout")}</Link>
                </div>
              </li>
            </ul>

            <div className="right-section">
              <div className="geobim-section">
                <div onClick={() => setShowGeoBIM(true)} style={{ cursor: "pointer" }}>
                  <img src={geoBIMImage} alt="GeoBIM" className="geobim-image" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
        <div id="saveProjectModal">
          <Modal show={showSaveProject} onHide={handleSaveProjectClose}>
            <Modal.Header closeButton>
              <Modal.Title>{t("projectList.saveModal.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group controlId="saveAs">
                  <Form.Check
                    value={PROJECT}
                    type="radio"
                    id="default-radio"
                    label={t("projectList.saveModal.text_1")}
                    onChange={handleRadio}
                    checked={formState.saveAs === PROJECT}
                    disabled={projectId === ""}
                  />

                  <Form.Check
                    value={AS}
                    type="radio"
                    label={t("projectList.saveModal.text_2")}
                    id="default-radio"
                    onChange={handleRadio}
                    checked={formState.saveAs === AS}
                  />
                </Form.Group>
                <Form.Control
                  className="mt-3"
                  type="text"
                  name="projectName"
                  placeholder={t("projectList.saveModal.newProjectPlaceholder")}
                  onChange={handleChange}
                  disabled={formState.saveAs === PROJECT}
                  value={formState.projectName}
                />
                {blockedChars.length > 0 && (
                  <div className="small text-danger mt-1">
                    {t("error.charSet", {
                      ns: "register",
                      chars: formatBlockedChars(blockedChars),
                    })}
                  </div>
                )}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleSaveProjectClose}>
                {t("projectList.cancel")}
              </Button>
              <Button
                variant="success"
                onClick={handleSaveProject}
                disabled={formState.projectName === ""}
              >
                {t("projectList.save")}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </nav>

      {showGeoBIM && (
        <div className="geobim-overlay" onClick={handleGeoBIMClose}>
          <div className="geobim-slide-panel" onClick={(e) => e.stopPropagation()}>
            <GeoBIM onClose={handleGeoBIMClose} />
          </div>
        </div>
      )}
    </>
  );
}

export default BerechnungNavigation;
